import React, { Fragment, useEffect, useState } from "react";
import { Col, Container, Row, Button } from "react-bootstrap";
import { FaPlus, FaDownload } from "react-icons/fa";
import toast from "react-hot-toast";
import { Shimmer } from "react-shimmer";
import { useNavigate } from "react-router-dom";

import { orderHeaders } from "../../constants/CONSTANT";
import JajotApi from "../../api/JajotApi";
import RecordDataTable from "../RecordDataTable";
import RecordModal from "../common/RecordModal";
import { TopNavigation } from "../layout/TopNavigation";
import OrderWizard from "./OrderWizard";
import TaskModal from "../common/TaskModal";

function Order() {
  const [orders, setOrders] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderCreateModal, setOrderCreateModal] = useState(false);
  const [orderDeleteModal, setOrderDeleteModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
    fetchLedgers();
  }, []);

  async function fetchOrders() {
    try {
      setIsLoading(true);
      const result = await JajotApi.getOrderRecords();
      if (result?.success) {
        setOrders(result.records || []);
      } else {
        setOrders([]);
        toast.error("No orders found");
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      toast.error("Failed to fetch orders");
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchLedgers() {
    try {
      const result = await JajotApi.getLedgerRecords();
      if (result?.success) {
        setLedgers(result.records || []);
      } else {
        setLedgers([]);
      }
    } catch (err) {
      console.error("Error fetching ledgers:", err);
      setLedgers([]);
    }
  }

  const navigateToEdit = (order) => {
    const mappedOrder = {
      details: {
        id: order.Id,
        date: order.Order_Date__c,
        number: order.Name,
        type: order.Type__c?.toLowerCase() || "Sales",
        status: order.Order_Status__c,
        partyLedger: order.Party_Ledger__r && {
          value: order.Party_Ledger__r.Id,
          label: order.Party_Ledger__r.Name,
        },
        salesLedger: order.Sales_Ledger__r && {
          value: order.Sales_Ledger__r.Id,
          label: order.Sales_Ledger__r.Name,
        },
      },
      items:
        order.Order_Line_Items__r?.records.map((r) => ({
          id: r.Id,
          isLedger: !!r.Ledger__r,
          item: r.Ledger__r
            ? { value: r.Ledger__r.Id, label: r.Ledger__r.Name }
            : { value: r.Stock_Item__r.Id, label: r.Stock_Item__r.Name },
          rate: r.Rate__c,
          quantity: r.Quantity__c,
          units: r.Stock_Item__r?.Units__c || "pcs",
          amount: r.Amount__c,
        })) || [],
    };

    navigate("/order/edit", { state: { existingOrder: mappedOrder } });
  };

  const handleDelete = (id) => {
    deleteRecord(id);
  };

  const handleStatusChange = (id, newStatus) => {
    const updatedOrders = orders.map((order) =>
      order.Id === id ? { ...order, Order_Status__c: newStatus } : order
    );
    setOrders(updatedOrders);
    toast.success("Order status Changed ");
  };

  async function deleteRecord(id) {
    try {
      const result = await JajotApi.deleteRecord("Order__c", id);
      if (!result.success) {
        alert("Failed to delete order");
        return;
      }
      setOrders((prev) => prev.filter((o) => o.Id !== id));
      toast.success("Order deleted");
    } catch (err) {
      console.error("Delete order error:", err);
      toast.error("Error deleting order");
    }
  }

  const ledgerOptions = [
    ...ledgers.map((l) => ({ value: l.Id, label: l.Name })),
  ];

  const filtersConfig = [
    { key: "Order_Date__c", label: "Start Date", type: "date" },
    { key: "EndDate", label: "End Date", type: "date" },
    {
      key: "Party_Ledger__c",
      label: "Party Ledger",
      type: "multiselect",
      options: ledgerOptions,
    },
    {
      key: "Type__c",
      label: "Type",
      type: "select",
      options: [
        { value: "Sales", label: "Sales" },
        { value: "Purchase", label: "Purchase" },
      ],
    },
  ];
  return (
    <Fragment>
      <TopNavigation active="Orders" />

      <Container fluid className="py-4">
        <div className="bg-white shadow-sm rounded p-3">
          <Row className="mb-4 align-items-center g-2">
            <Col xs={12} md>
              <h4 className="fw-bold text-primary mb-0">Orders Records</h4>
              <small className="text-muted">
                Manage and view all order items
              </small>
            </Col>
            <Col
              xs={12}
              md="auto"
              className="d-flex flex-wrap justify-content-end gap-2"
            >
              <Button
                variant="success"
                disabled={isLoading}
                onClick={() => setOrderCreateModal(true)}
              >
                <FaPlus className="me-1" /> Add New Order
              </Button>
              <Button
                className="fw-bold shadow-sm px-4"
                onClick={() => setShowTaskModal(true)}
                disabled={isLoading}
              >
                Add Task
              </Button>
            </Col>
          </Row>

          {isLoading ? (
            <div className="p-3">
              <Shimmer width={600} height={30} className="mb-3" />
              <Shimmer width={-1} height={20} className="mb-2" />
              <Shimmer width={-1} height={20} className="mb-2" />
              <Shimmer width={-1} height={20} />
            </div>
          ) : orders.length > 0 ? (
            <div className="table-responsive">
              <RecordDataTable
                tableHeaders={orderHeaders}
                tableData={orders}
                handleRowClick={setSelectedOrder}
                permission={{ update: true, delete: true }}
                filtersConfig={filtersConfig}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
                handleEdit={navigateToEdit}
              />
            </div>
          ) : (
            <div className="text-center text-muted py-5">
              <h6>No order records found</h6>
            </div>
          )}
        </div>
      </Container>
      {selectedOrder && (
        <RecordModal
          show={!!selectedOrder}
          onHide={() => setSelectedOrder(null)}
          record={selectedOrder}
          header={orderHeaders}
          defaultType="orders"
        />
      )}
      {orderCreateModal && (
        <OrderWizard
          show={orderCreateModal}
          onHide={() => setOrderCreateModal(false)}
        />
      )}
      {showTaskModal && (
        <TaskModal
          show={showTaskModal}
          onHide={() => setShowTaskModal(false)}
          records={orders}
          header="Order"
        />
      )}
    </Fragment>
  );
}

export default Order;
